/* eslint-disable react/no-unknown-property */
import { useRef, useMemo, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useCursor } from "@react-three/drei";
import * as THREE from "three";

interface AvatarProps {
  position: React.MutableRefObject<THREE.Vector3>;
  isMoving: React.MutableRefObject<boolean>;
  onClick?: () => void;
}

const ACCENT_COLOR = "#80bfff"; // Navy blue
const BODY_HEIGHT = 1.0;

export default function Avatar({ position, isMoving, onClick }: AvatarProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const prevPosition = useRef(new THREE.Vector3());
  const lookTarget = useMemo(() => new THREE.Vector3(), []);
  const tempObj = useMemo(() => new THREE.Object3D(), []);
  const tempQuat = useMemo(() => new THREE.Quaternion(), []);
  const camera = useThree((s) => s.camera);
  const [hovered, setHovered] = useState(false);
  useCursor(hovered);

  useFrame((state) => {
    if (!groupRef.current) return;
    const pos = position.current;

    groupRef.current.position.set(pos.x, pos.y, pos.z);

    if (!isMoving.current) {
      // Idle floating animation
      const floatY = Math.sin(state.clock.elapsedTime * 2) * 0.04;
      groupRef.current.position.y = pos.y + floatY;

      // Smoothly rotate so eyes (+Z face) point at the camera.
      // For Object3D.lookAt, local +Z faces the target — so aim straight at it.
      tempObj.position.set(pos.x, pos.y, pos.z);
      tempObj.lookAt(camera.position.x, pos.y, camera.position.z);
      tempQuat.copy(tempObj.quaternion);
      groupRef.current.quaternion.slerp(tempQuat, 0.12);
    } else {
      // While moving, face the direction of travel (eyes leading)
      const dx = pos.x - prevPosition.current.x;
      const dz = pos.z - prevPosition.current.z;
      if (Math.abs(dx) > 0.001 || Math.abs(dz) > 0.001) {
        lookTarget.set(pos.x + dx, pos.y, pos.z + dz);
        groupRef.current.lookAt(lookTarget);
      }
    }

    prevPosition.current.copy(pos);
  });

  return (
    <group
      ref={groupRef}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
    >
      {/* Avatar Body */}
      <mesh position={[0, BODY_HEIGHT + 0.0, 0]}>
        <capsuleGeometry args={[0.3, BODY_HEIGHT, 8, 16]} />
        <meshStandardMaterial
          color={ACCENT_COLOR}
          roughness={0.4}
          emissive={ACCENT_COLOR}
          emissiveIntensity={0.15}
        />
      </mesh>

      {/* Eyes — flat circle planes positioned in the top hemisphere of the capsule */}
      <mesh position={[-0.1, BODY_HEIGHT + 0.55, 0.295]}>
        <circleGeometry args={[0.04, 12]} />
        <meshBasicMaterial color="#000000" side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0.1, BODY_HEIGHT + 0.55, 0.295]}>
        <circleGeometry args={[0.04, 12]} />
        <meshBasicMaterial color="#000000" side={THREE.DoubleSide} />
      </mesh>

      {/* Ground shadow */}
      <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.2, 16]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.3} />
      </mesh>
    </group>
  );
}
